import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBrandGithub } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { safeGitHubUrl } from "./githubRenderUtils";
import { type GitHubUsersPayload, parseGitHubUsersPayload } from "./githubUsersSchema";

interface GitHubUsersRendererProps {
  block: StreamingBlock;
}

export const GitHubUsersRenderer = (props: GitHubUsersRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<GitHubUsersPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseGitHubUsersPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("github_users_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().users.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbBrandGithub class="size-4 shrink-0 text-primary-text" />
                <span class="truncate text-sm font-medium text-primary-text">
                  {rt.t("github_users_card.title", { repo: data().repo })}
                </span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().users.length === 1
                    ? rt.t("github_users_card.results_one")
                    : rt.t("github_users_card.results", { count: String(data().users.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().users}>
                  {(user) => (
                    <li class="px-3 py-2 text-sm">
                      <Show
                        when={safeGitHubUrl(user.url)}
                        fallback={<span class="truncate text-primary-text">{user.login}</span>}
                      >
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="truncate text-brand hover:underline"
                          >
                            {user.login}
                          </a>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>

              <div class="border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                {rt.t("github_users_card.assignable_note")}
              </div>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
